<template>
  <div v-if="!isLoading" id="pdf-aspect-ratio">
    <div
      class="pdf container-fluid overflow-auto"
      :style="{ backgroundColor: response.deletedAt ? '#e9e9e9' : 'white' }"
    >
      <!-- Quote Title -->
      <section class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12 flex justify-between items-center">
          <template v-if="transporter">
            <AvatarGroup v-if="getHeader.includes('Booking')" :list="getAvatarList()" />

            <div v-else>
              <q-avatar rounded size="100px" style="height: auto">
                <img
                  v-if="transporter.companyLogo"
                  class="logo q-mr-sm"
                  alt="company logo"
                  :src="transporter.companyLogo"
                />
                <img
                  v-else
                  class="logo q-mr-sm"
                  src="@/assets/icon_Personal.png"
                  alt="default profile picture"
                  style="filter: grayscale(100%)"
                />
              </q-avatar>
            </div>
          </template>

          <div v-else></div>

          <div class="flex column">
            <h4 class="text-right">
              {{ getHeader }}
            </h4>

            <div class="text-right text-weight-medium text-caption">
              <template v-if="showInvoice && transporter">
                <div><strong class="text-uppercase">invoice id: </strong>{{ response.invoice.id | formatId }}</div>

                <div class="text-uppercase"><strong>Transporter: </strong>{{ transporter.tradingName }}</div>

                <div v-if="transporter.abn"><strong class="text-uppercase">abn: </strong>{{ transporter.abn }}</div>

                <div>
                  <strong class="text-uppercase">date issued: </strong>{{ response.invoice.createdAt | formatDate }}
                </div>

                <div><strong class="text-uppercase">due date: </strong>{{ response.invoice.dueDate | formatDate }}</div>
              </template>

              <template v-else>
                <div>Document ID: {{ getDocId | formatId }}</div>
                <div v-if="hasResponse && transporter">
                  Transporter:
                  <span class="text-uppercase">
                    {{ truncate(transporter.tradingName) }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>

      <!-- Shipment - Request - Customer info -->
      <section class="row q-col-gutter-sm q-mb-sm">
        <!-- Customer Info -->
        <div v-if="showShipper && customer.name" class="col-6 flex items-stretch">
          <div class="box-list full-width">
            <h5 class="text-header">Customer</h5>

            <ul class="customer">
              <li class="info-item">
                <q-icon name="person" color="muted" />
                <span class="text-body1">{{ customer.name | capitalize }}</span>
              </li>
              <li class="info-item">
                <q-icon name="domain" color="muted" />
                <span class="text-body1 text-uppercase">{{ customer.businessName }}</span>
              </li>
              <li class="info-item">
                <q-icon name="email" color="muted" />
                <span class="text-body1">{{ customer.email }}</span>
              </li>
              <li class="info-item">
                <q-icon name="phone" color="muted" />
                <span class="text-body1">{{ customer.phone }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Request info -->
        <div v-else class="col-6">
          <div class="box-list full-width full-height">
            <h5 class="text-header">Request</h5>

            <ul class="customer">
              <li class="info-item">
                <span class="text-body1">
                  <span class="info-item-header" style="width: 9.25em"> Port of Operation: </span>
                  {{ request.port || get(request, 'containerType.port') }}
                </span>
              </li>
              <li class="info-item">
                <span class="text-body1">
                  <span class="info-item-header" style="width: 9.25em">Load type:</span>
                  {{ request.type | formatSlugToName }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Shipment info -->
        <div class="col-6">
          <div class="box-list">
            <h5 class="text-header">Info</h5>

            <ul class="customer">
              <li class="info-item">
                <span class="text-body1">
                  <span class="info-item-header">Shipment:</span>
                  {{ request.shipment || get(request, 'containerType.shipment') || 'Unknown' }}
                </span>
              </li>
              <li class="info-item">
                <span class="text-body1">
                  <span class="info-item-header">Other Ref.:</span>
                  {{ request.console || get(request, 'containerType.console') || 'Unknown' }}
                </span>
              </li>
              <li class="info-item">
                <span class="text-body1">
                  <span class="info-item-header">Vessel/Voy:</span>
                  {{ request.vessel || get(request, 'containerType.vessel') || 'Unknown' }}
                  /
                  {{ request.voyage || get(request, 'containerType.voyage') || 'Unknown' }}
                </span>
              </li>
              <li class="info-item">
                <span class="text-body1">
                  <span class="info-item-header">Date:</span>
                  {{ get(request, 'createdAt') | formatDate }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Delivery -->
      <section class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12">
          <div class="box-list">
            <h5 class="text-header">Delivery</h5>
            <div class="delivery">
              <div v-if="deliverTo" class="row ml-0 q-mb-sm mr-0">
                <div class="col-6 q-pa-none" :style="request.type === 'import' ? 'order: 1' : 'order: 4'">
                  <h6 class="delivery-header">
                    {{ request.type === 'import' ? 'Terminal-Pickup Full' : 'Delivery Terminal' }}
                  </h6>
                  <img class="q-mx-auto block" :src="terminalPic" style="height: 70px" />
                  <div v-if="!showInvoice" class="delivery-row">Contact: The Transport Manager</div>
                </div>

                <div class="col-6 q-pa-none" style="order: 2">
                  <h6 class="delivery-header">
                    {{ request.type === 'import' ? 'Deliver To Full' : 'Deliver To Empty' }}
                  </h6>
                  <div class="delivery-row">
                    {{ showDataIfShowShipper(deliverTo.companyName) | uppercase }}
                  </div>
                  <div class="delivery-row text-break">
                    {{ deliverTo.street | uppercase }}, {{ deliverTo.suburb | uppercase }},
                    {{ deliverTo.postCode }}
                  </div>
                  <div class="delivery-row">{{ deliverTo.city.name | uppercase }}</div>
                  <div v-if="!showInvoice" class="delivery-row">
                    Contact:
                    {{ showDataIfShowShipper(deliverTo.siteContactName) }}
                    - {{ showDataIfShowShipper(deliverTo.phone) }}
                  </div>
                </div>

                <div
                  v-if="!response.invoice || (deliverVia && emptyReturn === 'Not Sure Yet')"
                  class="col-6 q-pa-none"
                  style="order: 3; min-height: 125px"
                >
                  <h6 class="delivery-header">Deliver Via</h6>
                  <div v-if="deliverVia" class="content">
                    <div class="delivery-row">
                      {{ deliverVia.companyName ? deliverVia.companyName : deliverVia.company | uppercase }}
                    </div>
                    <div class="delivery-row">
                      {{ deliverVia.street | uppercase }}, {{ deliverVia.suburb | uppercase }},
                      {{ deliverVia.postCode }}
                    </div>
                    <div class="delivery-row">{{ deliverVia.city.name }}</div>
                    <div v-if="!showInvoice" class="delivery-row">
                      Contact: {{ showDataIfShowShipper(deliverVia.siteContactName) }} -
                      {{ showDataIfShowShipper(deliverVia.phone) }}
                    </div>
                  </div>
                </div>

                <div
                  v-if="!response.invoice || (deliverVia && emptyReturn === 'Not Sure Yet')"
                  class="col-6 q-pa-none position-relative"
                  :style="request.type === 'import' ? 'order: 4' : 'order: 1'"
                >
                  <h6 class="delivery-header">
                    {{ request.type === 'import' ? 'Empty Return Depot' : 'Empty Pick Up Depot' }}
                  </h6>
                  <div class="delivery-row">
                    {{ emptyReturn }}
                  </div>
                  <div v-if="emptyReturn !== 'Not Sure Yet' && !showInvoice" class="delivery-row">
                    Contact: The Transport Manager
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Container Details -->
      <section class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12">
          <div class="box-list">
            <h5 class="text-header">Container Details</h5>
            <div class="container-details">
              <table class="full-width">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Container No.</th>
                    <th scope="col">Container Type</th>
                    <th scope="col">Commodity</th>
                    <th scope="col">Hazardous</th>
                    <th scope="col">Gross Weight</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(container, idx) in containers" :key="container.id">
                    <td>{{ idx + 1 }}</td>
                    <td>
                      {{ container.containerCode ? container.containerCode : container.containerNumber }}
                    </td>
                    <td>
                      {{ container.type ? container.type : commodity.type | formatSlugToName }}
                    </td>
                    <td :style="{ textTransform: 'capitalize' }">
                      {{ commodity.commodity ? commodity.commodity : commodity.type }}
                    </td>
                    <td>
                      {{ commodity.hazardous ? `Class ${commodity.dgClass}` : 'No' }}
                    </td>
                    <td>{{ container.weight | formatWeight }} KG</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <!-- Job Options -->
      <section v-if="!showInvoice" class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12">
          <div class="box-list">
            <h5 class="text-header">Handling and Delivery Options</h5>

            <div class="row q-pa-sm">
              <div
                v-for="jobOption in jobOptions"
                :key="jobOption.l"
                class="col-4 flex justify-center items-center no-wrap"
              >
                <img :src="getSourceFromAsset(jobOption.i)" width="100px" />
                <span class="inline">{{ jobOption.l }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Instructions (optional) -->
      <section v-if="deliverInstruction && !showInvoice" class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12">
          <div class="box-list">
            <h5 class="text-header">Delivery Instructions</h5>
            <div class="delivery-instruction q-pa-sm q-ml-sm">
              <p class="q-mb-none text-uppercase">{{ deliverInstruction }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Invoice (if required) -->
      <section v-if="showInvoice" class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12">
          <div class="box-list">
            <div class="flex justify-between" style="background-color: #333">
              <h5 class="text-header">Invoice</h5>
              <h5 class="text-header">Due date: {{ response.invoice.dueDate | formatDate }}</h5>
            </div>
            <div class="container-details">
              <table class="full-width">
                <thead>
                  <tr>
                    <th scope="col" style="width: 50px">#</th>
                    <th scope="col" colspan="2">Cost Name</th>
                    <th scope="col" style="width: 115px">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(cost, idx) in response.invoice.costs" :key="idx">
                    <td>{{ idx + 1 }}</td>
                    <td colspan="2">{{ cost.name }}</td>
                    <td>{{ cost.price | formatCurrency }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="text-body2">
                    <td class="text-weight-bold text-left" colspan="2">Terms</td>
                    <td class="text-weight-bold text-right" style="width: 115px">Subtotal</td>
                    <td class="text-center" style="min-width: 100px">
                      {{ response.invoice.subTotal | formatCurrency }}
                    </td>
                  </tr>
                  <tr class="text-body2">
                    <td class="text-left" rowspan="2" colspan="2">
                      {{ response.tradingTerms[0] }}
                    </td>
                    <td class="text-weight-bold text-right">GST</td>
                    <td class="text-center">
                      {{ response.invoice.gst | formatCurrency }}
                    </td>
                  </tr>
                  <tr class="text-body2">
                    <td class="text-weight-bold text-right">Total</td>
                    <td class="text-weight-bold text-center">
                      {{ response.invoice.total | formatCurrency }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </section>

      <!-- Response (if exist) -->
      <section v-else-if="hasResponse" class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12">
          <div class="box-list">
            <div class="flex justify-between" :style="{ backgroundColor: '#333' }">
              <h5 class="text-header">Response</h5>
              <h5 v-if="isShownValidDate" class="text-header">
                Valid until: {{ response.quoteValidity | formatDate }}
              </h5>
              <h5 v-else></h5>
            </div>
            <div class="container-details">
              <table class="full-width">
                <thead>
                  <tr>
                    <th scope="col" style="width: 50px">#</th>
                    <th scope="col" colspan="2">Cost Name</th>
                    <th scope="col" style="width: 115px">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(cost, idx) in costs" :key="cost.id">
                    <td>{{ idx + 1 }}</td>
                    <td colspan="2">{{ cost.name }}</td>
                    <td>{{ cost.price | formatCurrency }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="text-body2">
                    <td class="text-weight-bold text-left" colspan="2">Terms</td>
                    <td class="text-weight-bold text-right" style="width: 115px">Subtotal</td>
                    <td class="text-center" style="min-width: 100px">
                      {{ response.subTotal | formatCurrency }}
                    </td>
                  </tr>
                  <tr class="text-body2">
                    <td class="text-left" rowspan="2" colspan="2">
                      {{ response.tradingTerms[0] }}
                    </td>
                    <td class="text-weight-bold text-right">GST</td>
                    <td class="text-center">{{ response.addGst | formatCurrency }}</td>
                  </tr>
                  <tr class="text-body2">
                    <td class="text-weight-bold text-right">Total</td>
                    <td class="text-weight-bold text-center">
                      {{ response.totalAud | formatCurrency }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section v-if="showInvoice && transporter" class="invoice row q-col-gutter-sm q-mb-sm">
        <div class="col-12 col-sm-4">
          <div class="box-list">
            <h5 class="text-header">Payment Details</h5>

            <ul class="customer">
              <li class="info-item">
                <span class="text-body1"> <strong>BSB: </strong> {{ transporter.bsb }} </span>
              </li>

              <li class="info-item">
                <span class="text-body1"> <strong>Account: </strong> {{ transporter.account }} </span>
              </li>

              <li class="info-item">
                <span class="text-body1">
                  <strong>Swift: </strong>
                  <template v-if="transporter.swift">{{ transporter.swift }}</template>
                  <template v-else>N/A</template>
                </span>
              </li>
            </ul>
          </div>
        </div>
        <span class="text-weight-medium text-body1"> Payment Reference: {{ response.invoice.id | formatId }} </span>
      </section>

      <!-- Bottom logo (view before download) -->
      <section v-if="isFromClient" class="row q-col-gutter-sm q-mb-sm">
        <div class="col-12">
          <div class="flex justify-end items-center q-gutter-x-sm">
            <h6 class="margin-right: 25px; font-weight: bold;">Powered by</h6>
            <picture>
              <source srcset="@/assets/logo.webp" type="image/webp" />
              <img src="@/assets/logo.png" width="240" alt="logo" />
            </picture>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import _get from 'lodash/get';
import _truncate from 'lodash/truncate';
import orderBy from 'lodash/orderBy';
import axios from 'axios';
import decode from 'jwt-decode';
import { mapState } from 'vuex';
import { getSourceFromAsset } from '@/utils';
import client from '@/plugins/client';

export default {
  name: 'PDFPreview',

  components: {
    AvatarGroup: () => import('@/components/AvatarGroup/AvatarGroup.vue'),
  },

  filters: {
    formatWeight(value) {
      // return typeof value === 'number'
      //   ? `${parseFloat(value)
      //       .toFixed(2)
      //       .replace(/(\d)(?=(\d{3})+\.)/g, '$1,')}`
      //   : value;

      // eslint-disable-next-line no-restricted-globals
      return isNaN(value) ? value : new Intl.NumberFormat('en-AU', { style: 'decimal' }).format(value);
    },
  },

  props: {
    requestFromClient: {
      type: Object,
      default: () => ({}),
    },
    responseFromClient: {
      type: Object,
      default: () => ({}),
    },
    isRFQ: {
      type: Boolean,
      default: false,
    },
    fakeTransporter: {
      type: Object,
      default: null,
    },
    isInvoice: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      request: this.requestFromClient,
      response: this.responseFromClient,
      transporter: this.fakeTransporter,
      isLoading: true,
      showInvoice: false,
    };
  },

  computed: {
    ...mapState(['user']),
    isFromClient() {
      return !!Object.keys(this.requestFromClient).length;
    },
    hasResponse() {
      return !!Object.keys(this.response).length;
    },
    getDocId() {
      return this.hasResponse ? this.response.id : this.request.id;
    },
    jobOptions() {
      const deliveryTypeList = [
        { v: 'side-lifter', l: 'Drop Out', i: 'Sidelifter.png' },
        { v: 'unpack', l: 'Wait For Unpack', i: 'WaitForUnpack.png' },
        { v: 'drop-trailer', l: 'Drop Trailer', i: 'DropTrailer.png' },
      ];
      const truckTypeList = [
        { v: 'side-loader', l: 'Side Loader', i: 'SideLoader.png' },
        { v: 'skel-trailer', l: 'Skel Trailer', i: 'SkelTrailer.png' },
        { v: 'tray-top', l: 'Tray Top', i: 'TrayTop.png' },
        { v: 'tipping-skel', l: 'Tipping Skel', i: 'TippingSkel.png' },
      ];
      const doorsPositionList = [
        { v: 'doors-to-rear', l: 'Doors To Rear', i: 'Doors_to_Rear.png' },
        { v: 'doors-to-cabin', l: 'Doors To Cabin', i: 'Doors_to_Cabin.png' },
      ];

      const truckTypeTemp = truckTypeList.find((type) => this.request.truckType === type.v);
      let deliveryTypeTemp;
      let doorsPositionTemp;
      if (Array.isArray(this.request.deliveryType)) {
        deliveryTypeTemp = deliveryTypeList.find((type) => this.request.deliveryType[0] === type.v);
      } else {
        deliveryTypeTemp = deliveryTypeList.find((type) => this.request.deliveryType === type.v);
      }
      const doorPos = this.request.doorsPosition || this.request.doorPosition;
      if (Array.isArray(doorPos)) {
        doorsPositionTemp = doorsPositionList.find((type) => doorPos[0] === type.v);
      } else {
        doorsPositionTemp = doorsPositionList.find((type) => doorPos === type.v);
      }

      return {
        truckType: truckTypeTemp,
        deliveryType: deliveryTypeTemp,
        doorsPosition: doorsPositionTemp,
      };
    },
    deliverTo() {
      return this.request.deliverTo || this.request.destination;
    },
    deliverVia() {
      return this.get(this.request, 'deliverVia') || this.get(this.request, 'destinationVia');
    },
    containers() {
      return this.get(this.request, 'containers', []) || this.request.containerType;
    },
    emptyReturn() {
      return this.get(this.request, 'emptyReturn');
    },
    deliverInstruction() {
      return this.get(this.request, 'note');
    },
    commodity() {
      return this.request.commodity || this.request.containerType;
    },
    customer() {
      return (
        this.request.customer ||
        this.request.shipper || {
          name: '',
          businessName: '',
          email: '',
          phone: '',
        }
      );
    },
    terminalPic() {
      const allTerminals = [
        { v: 'victoria', l: 'VICTORIA', i: 'Victoria_logo.png' },
        { v: 'dp-world', l: 'DP WORLD', i: 'DP_world_logo.png' },
        { v: 'patrick', l: 'PATRICK', i: 'Patrick_logo.png' },
        { v: 'hutchison', l: 'HUTCHISON', i: 'Hutchison_logo.png' },
        { v: 'flinders', l: 'FLINDERS', i: 'Flinders_logo.png' },
        { v: 'unknown', l: 'Unknown', i: 'Unknown_logo.png' },
      ];
      const selectedTerminal = this.request.pickupTerminal || this.request.terminal;

      return this.getSourceFromAsset(allTerminals.find((terminal) => terminal.v === selectedTerminal).i);
    },
    getHeader() {
      switch (true) {
        case this.showInvoice:
          return 'Tax Invoice';
        case !this.hasResponse:
          return 'Request For Quotation (RFQ)';
        case !this.response?.status:
          return 'Quotation (Road Delivery)';
        default:
          return 'Booking - Haulage Advice';
      }
    },
    // Only show validity date if pdf is a quotation
    isShownValidDate() {
      return this.getHeader.includes('Quotation');
    },
    showShipper() {
      return this.isFromClient
        ? this.$store.state.user?.type === 'admin' || this.response?.status
        : decode(this.$route.query.token).user.type === 'admin' || this.response?.status;
    },
    costs() {
      return orderBy(this.response.costs, ['order'], ['asc']);
    },
  },

  watch: {
    fakeTransporter(val) {
      this.transporter = val;
    },

    async getHeader() {
      this.getTransporterInfo();
    },
  },

  async created() {
    if (this.isFromClient) {
      this.showInvoice = this.isInvoice;
      this.isLoading = false;
      return;
    }

    const { id } = this.$route.params || this.props;
    const { token, response, forceRequest, isInvoice } = this.$route.query;
    const params = { forceRequest: !!forceRequest };

    if (response) params.response = response;
    if (isInvoice) params.isInvoice = isInvoice;

    try {
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/quoteRequest/${id}`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      this.request = data;

      if (response) {
        this.response =
          data.acceptedResponse?.id === response
            ? data.acceptedResponse
            : data.responses?.find((res) => res.id === response);
      }
    } catch (error) {
      if (error.response.status >= 400) {
        this.$router.replace('/');
      }
    } finally {
      this.isLoading = false;
      this.showInvoice = isInvoice === 'true';
    }
  },

  mounted() {
    this.getTransporterInfo();
  },

  methods: {
    get: _get,
    truncate: _truncate,
    getSourceFromAsset,
    showDataIfShowShipper(value) {
      let decodedUser;

      if (this.$route.query?.token) {
        decodedUser = decode(this.$route.query.token);
      }

      if (this.user?.type.startsWith('admin') || decodedUser?.user?.type === 'admin') {
        return value;
      }

      return this.showShipper ? value : new Array(value.length).fill('*').join('');
    },
    async getTransporterInfo() {
      if (!this.transporter && this.response.id) {
        const { id } = this.response;

        if (this.isFromClient) {
          const { data: transporter } = await client.get(`/quoteResponse/${id}/transporter`);

          this.transporter = transporter;
        } else {
          const { data: transporter } = await axios.get(
            `${process.env.VUE_APP_API_URL}/quoteResponse/${id}/transporter`,
            {
              headers: {
                Authorization: `Bearer ${this.$route.query.token}`,
              },
            }
          );

          this.transporter = transporter;
        }
      }
    },
    getAvatarList() {
      // Return shipper first (left avatar) then transporter (right avatar)
      return [{ ...this.customer }, { ...this.transporter }];
    },
  },
};
</script>

<style lang="scss">
#pdf-aspect-ratio {
  h1 {
    font-size: 2em;
  }
  h2 {
    font-size: 1.5em;
  }
  h5 {
    font-size: 1em;
  }
  h6,
  p {
    font-size: 0.8em;
  }
}
</style>

<style lang="scss" scoped>
.customer {
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  padding: 0;
  margin: 0;
  height: 100%;
  box-sizing: border-box;

  li {
    flex: 0;
    padding-left: 1em;
    padding-top: 0.5em;
    padding-bottom: 0.5em;
    box-sizing: border-box;
  }
}
.delivery {
  padding: 0;
}
.delivery-header {
  background: #737b81;
  color: white;
  padding-left: 0.5em;
  padding-right: 0.5em;
}
.delivery-row {
  padding: 0.5em 0.5em 0 0.5em;
  font-size: 0.8em;
  margin-bottom: 0;
}
.position-relative {
  position: relative;
  min-height: 125px;
  .bottom {
    position: absolute;
    bottom: 0;
  }
}
li {
  font-size: 1vw;
  line-height: 0.8vw;
  list-style: none;
  margin-left: 0;
  &:nth-child(odd) {
    background: #eaebeb;
  }
  &:nth-child(even) {
    background: #f5f5f5;
  }
}
.pdf {
  padding: 2rem;
  border: 1px solid #333;
  background: white;
  overflow-x: hidden;
  overflow-y: auto;

  th {
    text-align: center;
  }
  tr {
    font-size: 0.8em;
  }

  tbody tr {
    &:nth-child(odd) {
      td {
        background-color: #eaebeb !important;
      }
    }

    td {
      text-align: center;
      background-color: #f5f5f5 !important;
      padding: 0.25rem;
    }
  }

  tfoot {
    td {
      padding: 0.25rem;
    }
  }
}
.text-header {
  color: white;
  background: #333;
  padding: 0 1em;
  margin-bottom: 0;
  font-weight: 500;
}
.box-list {
  border: 1px solid #333;
  border-radius: 5px;
  padding: 0;
  overflow: hidden;
  .container-type {
    padding-left: 1em;
  }
}

.container-details {
  tr {
    #odd {
      background-color: rgb(253, 105, 105) !important;
    }
    #even {
      background-color: #f5f5f5 !important;
    }
  }
}

.info-item {
  > i {
    font-size: 1.25rem !important;
    margin: 0;
  }

  > span {
    margin-left: 0.5rem;
    word-break: break-word;
  }
}
.info-item-header {
  font-weight: 500;
  display: inline-block;
  width: 6.25em;
}

@media print {
  .pdf {
    padding: 0 2rem;
    position: relative;
    border: none;
    min-height: 100vh;
  }

  .text-body1 {
    font-size: 0.875rem;
  }

  .box-list {
    width: 100%;
  }

  tfoot {
    display: table-row-group;
  }

  .row {
    break-inside: avoid;
  }
}

.default-logo {
  max-width: 30%;
  height: 4rem;
  padding: 1rem;
  color: grey;
  text-transform: capitalize;
  word-wrap: break-word;
  border: 1px solid grey;
  border-radius: 0.5rem;
}
</style>
